import React from "react";

import { FieldQueryPredefinedControlType, FieldQueryProps, FieldQueryItem } from "./field-query.types";
import { getProcessedControls } from "./controls";

export const useFieldQueryController = <T,>(props: FieldQueryProps<T>) => {
  const { items, controls } = props;

  const getControl = React.useCallback(
    (type: FieldQueryPredefinedControlType) => {
      return controls.find((control) => control.type === type);
    },
    [controls]
  );

  const [selectedItemId, setSelectedItemId] = React.useState<FieldQueryItem["id"] | undefined>();

  const handleItemSelect = React.useCallback((item: FieldQueryItem) => {
    setSelectedItemId(item.id);
  }, []);

  const handleItemMove = React.useCallback(
    (direction: "up" | "down") => {
      if (selectedItemId) {
        const currentIndex = items.findIndex((f) => f.id === selectedItemId)!;
  
        if (currentIndex < 0 || currentIndex >= items.length) {
          return;
        }
  
        const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
  
        if (newIndex < 0 || newIndex >= items.length) {
          return;
        }
  
        const movedItem = items[currentIndex];
        const replacedItem = items[newIndex];
  
        if (!movedItem || !replacedItem) {
          return;
        }

        // Check if item has a parent field (table sub-field)
        // Treat undefined, null, and missing property as "no parent" (flat field)
        const getHasParent = (item: any): boolean => {
          if (!("parentField" in item)) {
            return false;
          }
          const parentField = item.parentField;
          return parentField !== null && parentField !== undefined;
        };

        const getParentKey = (item: any): string | number | null => {
          const parentField = item.parentField;
          if (!parentField) {
            return null;
          }
          return (
            parentField.id ??
            parentField.fieldid ??
            parentField.fieldId ??
            parentField.name ??
            parentField.fieldname ??
            parentField.fieldName ??
            null
          );
        };

        const movedHasParent = getHasParent(movedItem);
        const replacedHasParent = getHasParent(replacedItem);

        // Prevent swapping between flat fields and table sub-fields
        if (movedHasParent !== replacedHasParent) {
          return;
        }

        // If both are table sub-fields, ensure they're from the same parent table
        if (movedHasParent && replacedHasParent) {
          const movedParentKey = getParentKey(movedItem);
          const replacedParentKey = getParentKey(replacedItem);
          if (movedParentKey !== replacedParentKey) {
            return;
          }
        }

        // Enforce rule: all flat fields must be above all table sub-fields
        // If moving a table sub-field up, ensure we're not moving above any flat field
        if (movedHasParent && direction === "up") {
          // Check if there are any flat fields between the target position and current position
          for (let i = newIndex; i < currentIndex; i++) {
            const item = items[i];
            if (!getHasParent(item)) {
              // Found a flat field above the target position, cannot move
              return;
            }
          }
        }

        // If moving a flat field down, ensure we're not moving below any table sub-field
        if (!movedHasParent && direction === "down") {
          // Check if there are any table sub-fields between current position and target position
          for (let i = currentIndex + 1; i <= newIndex; i++) {
            const item = items[i];
            if (getHasParent(item)) {
              // Found a table sub-field below the current position, cannot move
              return;
            }
          }
        }
  
        const updatedItems = items.map((item, index): FieldQueryItem => {
          if (index === currentIndex && replacedItem) {
            return replacedItem;
          }
  
          if (index === newIndex && movedItem) {
            return movedItem;
          }
  
          return item;
        });
  
        const control = getControl(
          direction === "down" ? FieldQueryPredefinedControlType.moveDown : FieldQueryPredefinedControlType.moveUp
        );
  
        if (control?.handler) {
          control.handler(updatedItems);
        }
      }
    },
    [getControl, items, selectedItemId]
  );

  const handleAllRemoveClick = React.useCallback(() => {
    const control = getControl(FieldQueryPredefinedControlType.removeAll);

    if (control?.handler) {
      control.handler([]);
    }
  }, [getControl]);

  const processedControls = getProcessedControls({
    controls,
    items,
    selectedItemId,
    onAllItemsRemove: handleAllRemoveClick,
    onItemMove: handleItemMove,
  });

  return {
    selectedItemId,
    handleItemSelect,
    processedControls,
  };
};
